import { ChangeDetectionStrategy, Component, computed, effect, inject, signal, untracked } from '@angular/core';
import { NavigationError, RedirectCommand, Router } from '@angular/router';
import { ConnectivityService, ThemeService } from '@core/services';
import { OsButtonDirective, OsIconComponent } from '@shared/ui';

/**
 * Sem conexão: a tela pedida ainda não estava guardada no aparelho e a
 * internet caiu. Tenta de novo sozinha quando o sinal volta.
 *
 * Aparece no lugar da tela pedida, sem mudar o endereço (ver
 * `offlineNavigationHandler` em `app.config.ts`).
 */
@Component({
	selector: 'os-offline',
	imports: [OsButtonDirective, OsIconComponent],
	template: `
		<div class="status">
			<span class="status__logo"><img [src]="logo()" alt="ObraSync" /></span>
			<main class="status__main">
				<span class="status__mark" aria-hidden="true"><os-icon [size]="40" name="sinal" /></span>
				<div class="status__head" role="status">
					<h1>{{ online() ? 'O sinal voltou' : 'Sem conexão' }}</h1>
					<p>
						@if (online()) {
							Abrindo a tela de novo…
						} @else if (stillOffline()) {
							Ainda sem sinal. Assim que voltar, a tela abre sozinha.
						} @else {
							Esta tela precisa de internet para abrir. O que você registrar pelo Registrar fica guardado no aparelho e
							sobe quando o sinal voltar.
						}
					</p>
				</div>
				<div class="status__actions">
					<button (click)="retry()" osButton size="lg" type="button">
						<os-icon [size]="18" name="atualizar" />
						Tentar de novo
					</button>
					<button (click)="back()" osButton="secondary" size="lg" type="button">Voltar</button>
				</div>
			</main>
		</div>
	`,
	styleUrl: './status-page.scss',
	changeDetection: ChangeDetectionStrategy.OnPush,
})
export class OfflineComponent {
	private readonly router = inject(Router);
	private readonly theme = inject(ThemeService);
	readonly online = inject(ConnectivityService).online;
	readonly logo = computed(() => `assets/images/logo/logo-horizontal${this.theme.dark() ? '-brand' : ''}.png`);

	/** O endereço que a pessoa tentou abrir. */
	private readonly target: string =
		(this.router.currentNavigation()?.extras.state?.['url'] as string | undefined) ?? '/app/today';

	constructor() {
		effect(() => {
			if (this.online()) untracked(() => this.retry());
		});
	}

	readonly stillOffline = signal(false);

	/**
	 * Recarrega o endereço pedido. Não adianta só navegar de novo: o navegador
	 * guarda a falha do pedaço de código que não baixou até a página recarregar.
	 */
	retry(): void {
		if (!this.online()) {
			this.stillOffline.set(true);
			return;
		}
		window.location.assign(this.target);
	}

	back(): void {
		history.back();
	}
}

/** Endereço da tela de sem conexão (só aparece por este desvio, nunca na barra). */
export const OFFLINE_PATH = 'offline';

/**
 * Quando a navegação falha sem internet (a tela ainda não estava guardada
 * pelo service worker), mostra a tela de sem conexão no lugar, mantendo o
 * endereço pedido. Com internet, o erro segue o caminho normal.
 */
export function offlineNavigationHandler(error: NavigationError): RedirectCommand | void {
	if (typeof navigator === 'undefined' || navigator.onLine) return;
	const router = inject(Router);
	return new RedirectCommand(router.parseUrl(`/${OFFLINE_PATH}`), {
		skipLocationChange: true,
		state: { url: error.url },
	});
}
